import type { SyntheticEvent } from "react";
import { useCallback, useEffect, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import CircularProgress from "components/circular-progress";
import { useLazyGetStaffTaskHistoryQuery } from "services/staff-tasks";
import type {
  StaffTask,
  StaffTaskHistoryEvent
} from "services/types/staff-tasks.type";
import { formatDateToDateAndTime } from "utils/common";

// Palette tokens, never hex literals — the action color carries meaning
// (create = success, edit = warning, remove = error, status/collaborator
// movement = info) and must follow the theme.
const ACTION_COLORS: Record<string, string> = {
  Created: "success.main",
  Edited: "warning.main",
  Deleted: "error.main",
  "Collaborator Removed": "error.main",
  Restored: "info.main",
  "Status Changed": "info.main",
  "Collaborator Added": "info.main"
};

function formatEventDate(date: string) {
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return "--";
  }

  return formatDateToDateAndTime(parsedDate);
}

function eventDetails(event: StaffTaskHistoryEvent) {
  return event.changes ?? event.summary ?? "--";
}

function eventUserName(event: StaffTaskHistoryEvent) {
  return event.user?.full_name || event.user?.name || "System";
}

type StaffTaskHistoryProps = {
  task: StaffTask;
};

export default function StaffTaskHistory({ task }: StaffTaskHistoryProps) {
  const [expanded, setExpanded] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);
  const [loadHistory, { currentData, isFetching, isError }] =
    useLazyGetStaffTaskHistoryQuery();

  // The dialog stays mounted across task switches — without this reset the
  // panel would keep showing the previous task's events.
  useEffect(() => {
    setExpanded(false);
    setHasRequested(false);
  }, [task.id]);

  const handleChange = useCallback(
    (_event: SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded);
      if (isExpanded && !hasRequested) {
        setHasRequested(true);
        loadHistory(task.id);
      }
    },
    [hasRequested, loadHistory, task.id]
  );

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={handleChange}
      data-testid="staff-task-history"
      sx={{
        border: `1px solid ${grey[200]}`,
        borderRadius: "8px",
        "&:before": { display: "none" }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: "40px", px: 2 }}
      >
        <Typography variant="body2" fontWeight={600}>
          History
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        {isFetching && <CircularProgress sx={{ width: "100%", py: 1 }} />}
        {!isFetching && isError && (
          <Typography variant="body2" color="error">
            Unable to load task history.
          </Typography>
        )}
        {!isFetching && !isError && currentData?.length === 0 && (
          <Typography variant="body2" color={grey[700]}>
            No history recorded for this task.
          </Typography>
        )}
        {!isFetching && !isError && Boolean(currentData?.length) && (
          <Table size="small" data-testid="staff-task-history-table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>When</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Action</TableCell>
                <TableCell>Details</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentData?.map((event) => (
                <TableRow
                  key={event.id}
                  data-testid={`staff-task-history-row-${event.id}`}
                >
                  <TableCell sx={{ whiteSpace: "nowrap", color: grey[700] }}>
                    {formatEventDate(event.datetime)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography
                      variant="body2"
                      component="span"
                      fontWeight={500}
                      color={ACTION_COLORS[event.action] ?? "text.primary"}
                    >
                      {event.action}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>
                    {eventDetails(event)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {eventUserName(event)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
